import { getApiUrl } from '../utils/environment';
import type { WSClient } from '../ws/client';

export interface PagesProvisioningProfile {
  slug: string;
  pagesUrl: string;
  createdAt: string;
  version: number;
}

export interface PagesProvisioningStatus {
  profile: PagesProvisioningProfile;
  provisioning: {
    baseUrl: string;
    label: string;
    scopes: string[];
  };
  gateway: {
    serverName: string;
    transport: string;
    command?: string;
    args?: string[];
    cwd?: string | null;
  };
}

export interface PagesProvisioningBundle {
  profile: PagesProvisioningProfile;
  provisioning: {
    baseUrl: string;
    pagesUrl: string;
    apiKey: string;
    label: string;
    scopes: string[];
    issuedAt: string;
    key: {
      keyId: string;
      keyPrefix: string;
      label: string;
      scopes: string[];
      createdAt: string;
      lastUsedAt: string | null;
      isRevoked: boolean;
      revokedAt: string | null;
    };
  };
  gateway: {
    serverName: string;
    serverConfig: Record<string, unknown>;
  };
}

export interface GatewayConfigSnapshot {
  hash?: string | null;
  valid?: boolean;
  config?: Record<string, unknown>;
}

export interface ManagedPagesProvisioningMetadata {
  kind?: string;
  managedBy?: string;
  userId?: string;
  pagesUrl?: string;
  apiBaseUrl?: string;
  keyId?: string;
}

export interface ManagedPagesGatewayState {
  serverName: string;
  detectedServerName: string | null;
  extraManagedServerNames: string[];
  exists: boolean;
  managed: boolean;
  matchesUser: boolean;
  matchesPagesUrl: boolean;
  matchesRuntimeContract: boolean;
  metadata: ManagedPagesProvisioningMetadata | null;
  serverConfig: Record<string, unknown> | null;
}

interface ManagedPagesServerEntry {
  serverName: string;
  serverConfig: Record<string, unknown>;
  metadata: ManagedPagesProvisioningMetadata;
}

const LEGACY_ENV_KEYS = [
  'TALKYN_PAGES_BASE_URL',
  'TALKYN_PAGES_API_KEY',
  'TALKYN_PAGES_URL',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function arraysEqual(left: unknown, right: unknown): boolean {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

function normalizeManagedUserId(value: unknown): string | null {
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

function getManagedPagesMetadata(candidate: unknown): ManagedPagesProvisioningMetadata | null {
  if (!isRecord(candidate)) return null;

  const metadata = isRecord(candidate.talkynProvisioning)
    ? (candidate.talkynProvisioning as ManagedPagesProvisioningMetadata)
    : null;

  if (metadata?.kind !== 'pages' || metadata?.managedBy !== 'talkyn-native') {
    return null;
  }

  return metadata;
}

function listManagedPagesServers(servers: Record<string, unknown> | null): ManagedPagesServerEntry[] {
  if (!servers) return [];

  return Object.entries(servers).flatMap(([serverName, candidate]) => {
    if (!isRecord(candidate)) return [];
    const metadata = getManagedPagesMetadata(candidate);
    if (!metadata) return [];
    return [{
      serverName,
      serverConfig: candidate,
      metadata,
    }];
  });
}

function getManagedPagesServerNames(
  servers: Record<string, unknown> | null,
  userId?: string | number | null,
): string[] {
  const expectedUserId = normalizeManagedUserId(userId);

  return listManagedPagesServers(servers)
    .filter((entry) => expectedUserId == null || normalizeManagedUserId(entry.metadata.userId) === expectedUserId)
    .map((entry) => entry.serverName);
}

function resolveManagedPagesServer(params: {
  servers: Record<string, unknown> | null;
  preferredServerName: string;
  userId?: string | null;
}): { detectedServerName: string | null; serverConfig: Record<string, unknown> | null } {
  const preferred = params.servers && isRecord(params.servers[params.preferredServerName])
    ? (params.servers[params.preferredServerName] as Record<string, unknown>)
    : null;
  if (preferred) {
    return {
      detectedServerName: params.preferredServerName,
      serverConfig: preferred,
    };
  }

  const managedServerName = getManagedPagesServerNames(params.servers, params.userId)[0] ?? null;
  return {
    detectedServerName: managedServerName,
    serverConfig: managedServerName && params.servers && isRecord(params.servers[managedServerName])
      ? (params.servers[managedServerName] as Record<string, unknown>)
      : null,
  };
}

function buildReplacePatch(existing: unknown, next: unknown): unknown {
  if (!isRecord(next)) {
    return next;
  }

  const patch: Record<string, unknown> = {};
  const existingRecord = isRecord(existing) ? existing : {};

  for (const [key, value] of Object.entries(next)) {
    patch[key] = buildReplacePatch(existingRecord[key], value);
  }

  for (const key of Object.keys(existingRecord)) {
    if (!(key in next)) {
      patch[key] = null;
    }
  }

  return patch;
}

function buildUnsafeInstallMessage(
  serverName: string,
  metadata: ManagedPagesProvisioningMetadata | null,
): string {
  const target = `mcp.servers.${serverName}`;

  if (metadata) {
    return `Gateway already has a Talkyn-managed Pages server at ${target} for another user. Choose a different server name or update the gateway manually.`;
  }

  return `Gateway already has an unmanaged server at ${target}. Talkyn will not overwrite it automatically. Choose a different server name or update the gateway manually.`;
}

function assertSafePagesProvisioningInstall(params: {
  existingServerConfig: unknown;
  serverName: string;
  desiredUserId: string | null;
}): void {
  if (!params.existingServerConfig) return;

  const metadata = getManagedPagesMetadata(params.existingServerConfig);
  if (metadata && params.desiredUserId && normalizeManagedUserId(metadata.userId) === params.desiredUserId) {
    return;
  }

  throw new Error(buildUnsafeInstallMessage(params.serverName, metadata));
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = isRecord(payload) && typeof payload.error === 'string'
      ? payload.error
      : `Request failed (${response.status})`;
    throw new Error(message);
  }
  return payload as T;
}

export async function fetchPagesProvisioningStatus(
  headers: Record<string, string>,
): Promise<PagesProvisioningStatus> {
  const response = await fetch(`${getApiUrl()}/api/pages/provisioning`, {
    headers,
  });
  return readJsonResponse<PagesProvisioningStatus>(response);
}

export async function issuePagesProvisioningBundle(
  headers: Record<string, string>,
): Promise<PagesProvisioningBundle> {
  const response = await fetch(`${getApiUrl()}/api/pages/provisioning`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify({}),
  });
  return readJsonResponse<PagesProvisioningBundle>(response);
}

export async function readGatewayConfigSnapshot(client: WSClient): Promise<GatewayConfigSnapshot> {
  return client.request<Record<string, never>, GatewayConfigSnapshot>('config.get', {});
}

export function getManagedPagesGatewayState(params: {
  snapshot: GatewayConfigSnapshot | null | undefined;
  serverName: string;
  userId?: string | number | null;
  pagesUrl?: string | null;
  expectedCommand?: string | null;
  expectedArgs?: string[] | null;
  expectedCwd?: string | null;
  expectedBaseUrl?: string | null;
}): ManagedPagesGatewayState {
  const expectedUserId = normalizeManagedUserId(params.userId);
  const config = isRecord(params.snapshot?.config) ? params.snapshot?.config : null;
  const mcp = config && isRecord(config.mcp) ? config.mcp : null;
  const servers = mcp && isRecord(mcp.servers) ? mcp.servers : null;
  const managedServerNames = getManagedPagesServerNames(servers, expectedUserId);
  const { detectedServerName, serverConfig } = resolveManagedPagesServer({
    servers,
    preferredServerName: params.serverName,
    userId: expectedUserId,
  });
  const extraManagedServerNames = managedServerNames
    .filter((serverName) => serverName !== detectedServerName);

  const metadata = getManagedPagesMetadata(serverConfig);
  const actualEnv = serverConfig && isRecord(serverConfig.env)
    ? (serverConfig.env as Record<string, unknown>)
    : null;
  const actualCwd = typeof serverConfig?.cwd === 'string'
    ? serverConfig.cwd
    : (typeof serverConfig?.workingDirectory === 'string' ? serverConfig.workingDirectory : null);
  const expectedCwd = params.expectedCwd ?? null;
  const hasLegacyEnvKeys = actualEnv
    ? LEGACY_ENV_KEYS.some((key) => Object.prototype.hasOwnProperty.call(actualEnv, key))
    : false;
  const matchesRuntimeContract = Boolean(
    serverConfig
    && detectedServerName === params.serverName
    && extraManagedServerNames.length === 0
    && (!params.expectedCommand || serverConfig.command === params.expectedCommand)
    && (!params.expectedArgs || arraysEqual(serverConfig.args, params.expectedArgs))
    && actualCwd === expectedCwd
    && actualEnv
    && (!params.expectedBaseUrl || actualEnv.PAGES_API_BASE_URL === params.expectedBaseUrl)
    && (!params.pagesUrl || actualEnv.PAGES_SITE_URL === params.pagesUrl)
    && typeof actualEnv.PAGES_API_KEY === 'string'
    && actualEnv.PAGES_API_KEY.trim().length > 0
    && !hasLegacyEnvKeys
  );

  return {
    serverName: params.serverName,
    detectedServerName,
    extraManagedServerNames,
    exists: Boolean(serverConfig),
    managed: Boolean(metadata),
    matchesUser: expectedUserId == null
      ? Boolean(normalizeManagedUserId(metadata?.userId))
      : normalizeManagedUserId(metadata?.userId) === expectedUserId,
    matchesPagesUrl: params.pagesUrl == null ? Boolean(metadata?.pagesUrl) : metadata?.pagesUrl === params.pagesUrl,
    matchesRuntimeContract,
    metadata,
    serverConfig,
  };
}

export async function installPagesProvisioningOnGateway(
  client: WSClient,
  bundle: PagesProvisioningBundle,
  options: { sessionKey?: string | null } = {},
): Promise<void> {
  const snapshot = await readGatewayConfigSnapshot(client);
  const baseHash = typeof snapshot.hash === 'string' ? snapshot.hash.trim() : '';
  if (!baseHash) {
    throw new Error('Gateway config hash unavailable. Reconnect and try again.');
  }

  const existingConfig = isRecord(snapshot.config) ? snapshot.config : null;
  const existingMcp = existingConfig && isRecord(existingConfig.mcp) ? existingConfig.mcp : null;
  const existingServers = existingMcp && isRecord(existingMcp.servers) ? existingMcp.servers : null;
  const existingServerConfig = existingServers?.[bundle.gateway.serverName];
  const desiredUserId = normalizeManagedUserId(getManagedPagesMetadata(bundle.gateway.serverConfig)?.userId);

  assertSafePagesProvisioningInstall({
    existingServerConfig,
    serverName: bundle.gateway.serverName,
    desiredUserId,
  });

  const serverPatch = buildReplacePatch(existingServerConfig, bundle.gateway.serverConfig);
  const legacyManagedServerNames = getManagedPagesServerNames(existingServers, desiredUserId)
    .filter((serverName) => serverName !== bundle.gateway.serverName);
  const serversPatch: Record<string, unknown> = {
    [bundle.gateway.serverName]: serverPatch,
  };

  for (const serverName of legacyManagedServerNames) {
    serversPatch[serverName] = null;
  }

  const patch = {
    mcp: {
      servers: serversPatch,
    },
  };

  await client.request('config.patch', {
    baseHash,
    raw: JSON.stringify(patch, null, 2),
    sessionKey: options.sessionKey ?? undefined,
    note: 'Talkyn Pages provisioning',
  });
}
